import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FiX, FiCamera, FiUpload } from 'react-icons/fi';
import { getInfluencerProfileRequest } from '../../../store/authStore/actions/authActions/getInfluencerProfileActions';
import { saveInfluencerProfileRequest, saveInfluencerProfileReset } from '../../../store/authStore/actions/authActions/saveInfluencerProfileActions';
import { uploadProfilePhotoRequest } from '../../../store/authStore/actions/authActions/uploadProfilePhotoActions';
import { deactivateAccountRequest, deactivateAccountReset } from '../../../store/authStore/actions/authActions/deactivateAccountActions';
import { logout } from '../../../store/publicStore/actions/utilsActions/utilsActions';
import { getS3ImageUrl } from '../../../utils/imageUtils';
import './ProfilePanel.css';

const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55+'];
const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const LANGUAGES = [
    'English', 'Hindi', 'Spanish', 'French', 'German', 'Portuguese',
    'Arabic', 'Mandarin', 'Japanese', 'Korean', 'Tamil', 'Telugu',
    'Bengali', 'Marathi', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi',
    'Urdu', 'Italian', 'Russian', 'Dutch', 'Turkish', 'Thai', 'Vietnamese'
];

const FIELD_HELPER_TEXT = {
    ageRange: 'Select the age group that most of your viewers belong to.',
    gender: 'Select the gender that best represents your viewer base.',
    city: 'Enter the city where most of your viewers are located.',
    language: 'Select the primary language of your content/platform.',
    photo: {
        INFLUENCER: 'Profiles with photos build more trust with brands.',
        AGENCY: 'A profile photo helps build credibility with influencers.',
        BRAND: 'Add a photo to make your brand more recognizable.'
    }
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];



const ProfilePanel = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const getUserInfoReducer = useSelector(state => state.getUserInfoReducer);
    const getInfluencerProfileReducer = useSelector(state => state.getInfluencerProfileReducer);
    const saveInfluencerProfileReducer = useSelector(state => state.saveInfluencerProfileReducer);
    const uploadProfilePhotoReducer = useSelector(state => state.uploadProfilePhotoReducer);
    const deactivateAccountReducer = useSelector(state => state.deactivateAccountReducer);

    const [formData, setFormData] = useState({
        ageRange: '',
        gender: '',
        city: '',
        language: '',
        photoUrl: '',
    });

    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoError, setPhotoError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [isSavingUI, setIsSavingUI] = useState(false);
    const isSubmitting = useRef(false);

    const userType = getUserInfoReducer?.data?.userType;
    const isInfluencer = userType === 'INFLUENCER';

    // Fetch profile on open and reset local state
    useEffect(() => {
        if (isOpen) {
            dispatch(getInfluencerProfileRequest());
            setPhotoPreview(null);
            setPhotoError('');
            setSaveSuccess(false);
            setSaveError('');
        }
    }, [isOpen, dispatch]);

    // Pre-fill form when profile data loads
    useEffect(() => {
        if (getInfluencerProfileReducer?.data) {
            const d = getInfluencerProfileReducer.data;
            setFormData({
                ageRange: d.ageRange || '',
                gender: d.profileGender || '',
                city: d.city || '',
                language: d.language || '',
                photoUrl: d.profilePhotoUrl || '',
            });
            if (d.profilePhotoUrl) {
                setPhotoPreview(null); // Use existing URL from server
            }
        }
    }, [getInfluencerProfileReducer?.data]);

    // Handle save response errors (success handled via callback)
    useEffect(() => {
        if (saveInfluencerProfileReducer?.status && saveInfluencerProfileReducer?.status !== 200 && saveInfluencerProfileReducer?.status !== 'loading') {
            setSaveError(saveInfluencerProfileReducer?.message || 'Failed to save profile');
            setTimeout(() => setSaveError(''), 5000);
            setIsSavingUI(false);
            isSubmitting.current = false;
            dispatch(saveInfluencerProfileReset());
        }
    }, [saveInfluencerProfileReducer?.status, saveInfluencerProfileReducer?.message, dispatch]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handlePhotoSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPhotoError('');

        // Validate type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setPhotoError('Only JPEG, PNG, and GIF files are allowed.');
            return;
        }

        // Validate size
        if (file.size > MAX_FILE_SIZE) {
            setPhotoError('File size must be under 5MB.');
            return;
        }

        // Preview
        const reader = new FileReader();
        reader.onload = (ev) => {
            setPhotoPreview(ev.target.result);
        };
        reader.readAsDataURL(file);

        // Upload
        dispatch(uploadProfilePhotoRequest(
            { file },
            (response, status) => {
                if (status === 200) {
                    setFormData(prev => ({ ...prev, photoUrl: response }));
                } else {
                    setPhotoError('Upload failed. Please try again.');
                }
            }
        ));
    }, [dispatch]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            // Simulate file input select
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            if (fileInputRef.current) {
                fileInputRef.current.files = dataTransfer.files;
                handlePhotoSelect({ target: { files: dataTransfer.files } });
            }
        }
    }, [handlePhotoSelect]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleSave = useCallback(() => {
        if (isSubmitting.current) return;
        isSubmitting.current = true;
        setIsSavingUI(true);
        setSaveSuccess(false);
        setSaveError('');
        dispatch(saveInfluencerProfileRequest({
            ageRange: formData.ageRange || null,
            gender: formData.gender || null,
            city: formData.city || null,
            language: formData.language || null,
            photoUrl: formData.photoUrl || null,
        }, (response, status) => {
            isSubmitting.current = false;
            setIsSavingUI(false);
            if (status === 200) {
                setSaveSuccess(true);
                setSaveError('');
                dispatch(getInfluencerProfileRequest());
                dispatch(saveInfluencerProfileReset());
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        }));
    }, [dispatch, formData]);

    const handleDeactivate = useCallback(() => {
        const confirmDeactivate = window.confirm(
            'Are you sure you want to deactivate your account? This action will disable your access and you will be logged out. Please contact support if you wish to reactivate later.'
        );

        if (confirmDeactivate) {
            dispatch(deactivateAccountRequest({}, (response, status, message) => {
                if (status === 200) {
                    alert('Your account has been deactivated. You will now be logged out.');
                    dispatch(logout());
                    onClose();
                } else {
                    alert(message || 'Failed to deactivate account. Please try again later.');
                }
            }));
        }
    }, [dispatch, onClose]);

    const handleClose = useCallback(() => {
        dispatch(deactivateAccountReset());
        onClose();
    }, [onClose, dispatch]);

    // Close on Escape key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    const profileData = getInfluencerProfileReducer?.data;
    const isLoading = getInfluencerProfileReducer?.loading;
    const isSaving = saveInfluencerProfileReducer?.loading;
    const isUploading = uploadProfilePhotoReducer?.loading;

    // Check if profile is incomplete (any of the 4 fields missing)
    const isProfileIncomplete = isInfluencer && (() => {
        const d = getInfluencerProfileReducer?.data;
        if (!d) return true;
        return !d.ageRange || !d.profileGender || !d.city || !d.language;
    })();

    const getUserTypeBadge = (type) => {
        const cls = (type || '').toLowerCase();
        return (
            <span className={`profile-user-type-badge ${cls}`}>
                {type}
            </span>
        );
    };

    return (
        <>
            <div className="profile-overlay" onClick={handleClose} />
            <div className="profile-panel" role="dialog" aria-label="User Profile">
                {/* Header */}
                <div className="profile-panel-header">
                    <h2>My Profile</h2>
                    <button className="profile-panel-close" onClick={handleClose} aria-label="Close">
                        <FiX />
                    </button>
                </div>

                {/* Body */}
                <div className="profile-panel-body">
                    {isLoading && !profileData ? (
                        <div className="profile-spinner">
                            <div className="profile-spinner-circle" />
                        </div>
                    ) : (
                        <>

                            {/* Read-only User Info */}
                            <div className="profile-section">
                                <div className="profile-section-title">Account Information</div>

                                <div className="profile-info-row">
                                    <span className="profile-info-label">First Name</span>
                                    <span className="profile-info-value">
                                        {profileData?.firstName || getUserInfoReducer?.data?.firstName || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">Last Name</span>
                                    <span className="profile-info-value">
                                        {profileData?.lastName || getUserInfoReducer?.data?.lastName || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">Email</span>
                                    <span className="profile-info-value">
                                        {profileData?.email || getUserInfoReducer?.data?.email || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">Country</span>
                                    <span className="profile-info-value">
                                        {profileData?.countryName || getUserInfoReducer?.data?.country?.countryName || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">Phone</span>
                                    <span className="profile-info-value">
                                        {profileData?.phoneNumber || getUserInfoReducer?.data?.phoneNumber || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">Gender</span>
                                    <span className="profile-info-value">
                                        {profileData?.userGender || getUserInfoReducer?.data?.gender || '—'}
                                    </span>
                                </div>
                                <div className="profile-info-row">
                                    <span className="profile-info-label">User Type</span>
                                    <span className="profile-info-value">
                                        {getUserTypeBadge(profileData?.userType || getUserInfoReducer?.data?.userType)}
                                    </span>
                                </div>
                            </div>

                            {/* Editable Influencer Profile — only for Influencer users */}
                            {isInfluencer && isProfileIncomplete && (
                                <div className="profile-section">
                                    <div className="profile-incomplete-hint">
                                        <span className="profile-incomplete-hint-icon">ℹ️</span>
                                        <span>Please complete your audience demographics to help brands find you.</span>
                                    </div>
                                </div>
                            )}

                            {/* Profile Photo - Visible for all user types */}
                            <div className="profile-section">
                                <div className="profile-section-title">Profile Picture</div>
                                <div className="profile-form-group">
                                    <div className="profile-photo-upload">
                                        <div
                                            className="profile-photo-preview"
                                            onClick={() => fileInputRef.current?.click()}
                                            onDrop={handleDrop}
                                            onDragOver={handleDragOver}
                                        >
                                            {photoPreview ? (
                                                <img src={photoPreview} alt="Profile preview" />
                                            ) : formData.photoUrl ? (
                                                <img src={getS3ImageUrl(formData.photoUrl)} alt="Profile" />
                                            ) : (
                                                <div className="profile-photo-placeholder">
                                                    <FiCamera className="photo-icon" />
                                                    <span>Click or drag</span>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif"
                                            style={{ display: 'none' }}
                                            onChange={handlePhotoSelect}
                                        />
                                        <button
                                            className="profile-photo-upload-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                        >
                                            {isUploading ? (
                                                'Uploading...'
                                            ) : (
                                                <>
                                                    <FiUpload style={{ marginRight: '0.4rem' }} />
                                                    Choose Photo
                                                </>
                                            )}
                                        </button>
                                        {photoError && <div className="profile-photo-error">{photoError}</div>}
                                        <div className="profile-form-helper" style={{ textAlign: 'center', marginTop: '1rem', display: 'block' }}>
                                            {FIELD_HELPER_TEXT.photo[userType] || FIELD_HELPER_TEXT.photo.INFLUENCER}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Influencer Specific Fields */}
                            {isInfluencer && (
                                <div className="profile-section">
                                    <div className="profile-section-title">Audience Demographics</div>

                                    {/* Viewer Age Group */}
                                    <div className="profile-form-group">
                                        <label>Viewer Age Group</label>
                                        <select
                                            className="profile-form-select"
                                            name="ageRange"
                                            value={formData.ageRange}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select viewer age group</option>
                                            {AGE_RANGES.map(r => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                        <span className="profile-form-helper">{FIELD_HELPER_TEXT.ageRange}</span>
                                    </div>

                                    {/* Viewers Gender */}
                                    <div className="profile-form-group">
                                        <label>Viewers Gender</label>
                                        <select
                                            className="profile-form-select"
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select viewers gender</option>
                                            {GENDERS.map(g => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                        <span className="profile-form-helper">{FIELD_HELPER_TEXT.gender}</span>
                                    </div>

                                    {/* Viewer City */}
                                    <div className="profile-form-group">
                                        <label>Viewer City</label>
                                        <input
                                            className="profile-form-input"
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            placeholder="Enter your viewers' city"
                                        />
                                        <span className="profile-form-helper">{FIELD_HELPER_TEXT.city}</span>
                                    </div>

                                    {/* Platform Language */}
                                    <div className="profile-form-group">
                                        <label>Platform Language</label>
                                        <select
                                            className="profile-form-select"
                                            name="language"
                                            value={formData.language}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select platform language</option>
                                            {LANGUAGES.map(l => (
                                                <option key={l} value={l}>{l}</option>
                                            ))}
                                        </select>
                                        <span className="profile-form-helper">{FIELD_HELPER_TEXT.language}</span>
                                    </div>
                                </div>
                            )}

                            {/* Status Messages */}
                            <div className="profile-section" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                                {saveSuccess && (
                                    <div className="profile-success-msg" style={{ marginBottom: '1rem' }}>
                                        ✓ Profile saved successfully!
                                    </div>
                                )}
                                {saveError && (
                                    <div className="profile-error-msg" style={{ marginBottom: '1rem' }}>
                                        {saveError}
                                    </div>
                                )}
                            </div>

                            {/* Save Button - Visible for all user types */}
                            <div className="profile-section" style={{ borderTop: 'none', marginTop: '-2rem' }}>
                                <button
                                    className="profile-save-btn"
                                    onClick={handleSave}
                                    disabled={isSaving || isUploading || isSavingUI}
                                >
                                    {isSaving || isSavingUI ? 'Saving...' : 'Save Profile'}
                                </button>
                            </div>

                            {/* Danger Zone */}
                            <div className="profile-section">
                                <div className="profile-danger-zone">
                                    <div className="profile-danger-zone-title">Danger Zone</div>
                                    <div className="profile-danger-zone-desc">
                                        Once you deactivate your account, you will no longer be able to log in.
                                        You'll need to contact support to reactivate it.
                                    </div>
                                    <button
                                        className="profile-deactivate-btn"
                                        onClick={handleDeactivate}
                                        disabled={deactivateAccountReducer?.loading}
                                    >
                                        {deactivateAccountReducer?.loading ? 'Deactivating...' : 'Deactivate Account'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ProfilePanel;
